import React, { useId } from "react";
import { formatMetricCount } from "../utils/micrositeMetricFormat";

/*
 * RECOMMENDED DATA LAYERS — provider-reported audience signals on the strategy
 * microsite. Byte-identical in Cortex and Quantum.
 *
 * One renderer, two callers:
 *   - the proposal story (components/CampaignProposalPlanning) renders its
 *     data signal groups through DataLayerGroupGrid with the proposal's CTV
 *     bid guidance — the owner disclosed that proposal's economics on every
 *     surface (Campaign 53);
 *   - a campaign bound to a reviewed data-layers record renders
 *     `campaign.sections.recommendedDataLayers` (backend
 *     campaign_recommended_data_layers_v1) as a section of its own
 *     (CampaignRecommendedDataLayers below).
 *
 * PRICING is a presentation decision carried by the data, never a missing
 * field: the section renders a pricing row only when its record says pricing
 * is shown (`presentation.showPricing === true`) AND the segment carries a
 * served price. A record that withholds pricing is served with no price at
 * all — the backend projection omits it — so there is nothing to hide here.
 *
 * SIZES are each provider's own audience size, labelled as provider-reported,
 * never summed, never called reach, people or households.
 */

export const RECOMMENDED_DATA_LAYERS_SCHEMA_VERSION = "campaign_recommended_data_layers_v1";

export const DATA_LAYERS_KICKER = "Provider / Data Signals";
export const DATA_LAYERS_TITLE = "Recommended Data Layers";
export const DATA_LAYERS_META = "Provider-reported sizes";
export const DATA_LAYERS_SIZE_LABEL = "Provider-reported audience size";
export const DATA_LAYERS_INTRO =
  "Provider-reported audience sizes use each provider's own definition. They are not people, households, unique reach or delivery forecasts, and they are never added together.";

const asArray = (value) => (Array.isArray(value) ? value : []);
const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);
const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;
const isProviderSize = (value) => Number.isSafeInteger(value) && value > 0;

export const formatDataLayerSize = (value) => (isFiniteNumber(value) ? formatMetricCount(value) : "");

const formatDataLayerCpm = (value) =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "2026-10-02" → "October 2, 2026"; anything else → "".
export const formatDataLayerObservationDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "";
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${month} ${Number(match[3])}, ${match[1]}` : "";
};

/*
 * The group grid. `pricingFor(segment)` returns the segment's pricing row
 * ({ label, value }) or null; without it no pricing row can render. The markup
 * is the proposal story's data signals markup, unchanged (Campaign 53's page
 * is frozen; RecommendedDataLayers.test.js pins it against the pristine
 * capture).
 */
export function DataLayerGroupGrid({ groups, pricingFor = null }) {
  return (
    <div className="campaign-strategy-microsite-proposal-signal-grid">
      {asArray(groups).map((group) => (
        <article
          key={group.key}
          className={`campaign-strategy-microsite-chart-card campaign-strategy-microsite-proposal-signal-group is-${group.key}`}
        >
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title">{group.title}</span>
          </div>
          {group.note ? <p className="campaign-strategy-microsite-proposal-note">{group.note}</p> : null}
          <ul className="campaign-strategy-microsite-proposal-signal-list">
            {asArray(group.segments).map((segment) => {
              const pricing = typeof pricingFor === "function" ? pricingFor(segment) : null;
              return (
                <li key={segment.label} className="campaign-strategy-microsite-proposal-signal">
                  <div className="campaign-strategy-microsite-proposal-signal-head">
                    <span className="campaign-strategy-microsite-proposal-signal-label">{segment.label}</span>
                    {segment.role ? (
                      <span className="campaign-strategy-microsite-proposal-role">{segment.role}</span>
                    ) : null}
                  </div>
                  <span className="campaign-strategy-microsite-proposal-signal-copy">{segment.signal}</span>
                  <dl className="campaign-strategy-microsite-proposal-signal-figures">
                    <div>
                      <dt>{DATA_LAYERS_SIZE_LABEL}</dt>
                      <dd>{formatDataLayerSize(segment.size)}</dd>
                    </div>
                    {pricing ? (
                      <div>
                        <dt>{pricing.label}</dt>
                        <dd>{pricing.value}</dd>
                      </div>
                    ) : null}
                  </dl>
                </li>
              );
            })}
          </ul>
        </article>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The standalone section — resolver (fail closed) and component.
// ---------------------------------------------------------------------------

const isServedPrice = (pricing) =>
  Boolean(pricing) &&
  isNonEmptyString(pricing.label) &&
  isFiniteNumber(pricing.workingCpm) &&
  isFiniteNumber(pricing.clientCpm);

/*
 * A served section renders only whole: a known schema, one to three groups,
 * every segment with a label, a description and a positive provider size. A
 * malformed section renders nothing, never half a recommendation. Pricing is
 * kept only when the record's presentation shows it.
 */
export const resolveRecommendedDataLayers = (campaign) => {
  const section = campaign?.sections?.recommendedDataLayers;
  if (!section || section.schemaVersion !== RECOMMENDED_DATA_LAYERS_SCHEMA_VERSION) return null;
  const groups = asArray(section.groups);
  if (!groups.length || groups.length > 3) return null;
  const labels = new Set();
  const complete = groups.every(
    (group) =>
      isNonEmptyString(group?.key) &&
      isNonEmptyString(group?.title) &&
      asArray(group?.segments).length > 0 &&
      group.segments.every((segment) => {
        if (!isNonEmptyString(segment?.label) || !isNonEmptyString(segment?.signal)) return false;
        if (!isProviderSize(segment.size)) return false;
        const key = segment.label.trim().toLowerCase();
        if (labels.has(key)) return false;
        labels.add(key);
        return true;
      })
  );
  if (!complete) return null;
  const showPricing = section.presentation?.showPricing === true;
  return {
    showPricing,
    observationLabel: formatDataLayerObservationDate(section.observation?.asOfDate),
    geography: isNonEmptyString(section.observation?.geography) ? section.observation.geography.trim() : "",
    groups: groups.map((group) => ({
      key: group.key,
      title: group.title,
      note: isNonEmptyString(group.note) ? group.note : "",
      segments: group.segments.map((segment) => ({
        label: segment.label,
        role: isNonEmptyString(segment.role) ? segment.role : "",
        signal: segment.signal,
        size: segment.size,
        ...(showPricing && isServedPrice(segment.pricing) ? { pricing: { ...segment.pricing } } : {}),
      })),
    })),
  };
};

// The snapshot line: when the catalogue was read (UTC, the provenance clock),
// which geography the sizes describe, and that the catalogue can move.
export const describeDataLayersObservation = ({ observationLabel, geography }) =>
  [
    `Provider catalogue snapshot, observed ${observationLabel} (UTC).`,
    geography ? `Sizes are the provider's ${geography} figures, before the campaign's market geography is applied.` : "",
    "Availability and size can change before activation.",
  ]
    .filter(Boolean)
    .join(" ");

const servedPricingRow = (segment) =>
  isServedPrice(segment.pricing)
    ? {
        label: segment.pricing.label,
        value: `${formatDataLayerCpm(segment.pricing.workingCpm)} working · ${formatDataLayerCpm(segment.pricing.clientCpm)} client`,
      }
    : null;

export default function CampaignRecommendedDataLayers({ dataLayers }) {
  const headingId = useId();
  if (!dataLayers) return null;
  return (
    <div className="campaign-strategy-microsite-data-layers" data-testid="campaign-recommended-data-layers">
      <section
        className="campaign-strategy-microsite-section-card campaign-strategy-microsite-proposal-section campaign-strategy-microsite-proposal-section--data-signals"
        aria-labelledby={headingId}
        data-data-layers-section="recommended-data-layers"
      >
        <div className="campaign-strategy-microsite-section-head">
          <div>
            <span className="campaign-strategy-microsite-section-kicker">{DATA_LAYERS_KICKER}</span>
            <h3 id={headingId} className="campaign-strategy-microsite-section-title">
              {DATA_LAYERS_TITLE}
            </h3>
          </div>
          <span className="campaign-strategy-microsite-section-meta">{DATA_LAYERS_META}</span>
        </div>
        <p className="campaign-strategy-microsite-section-intro">{DATA_LAYERS_INTRO}</p>
        <DataLayerGroupGrid groups={dataLayers.groups} pricingFor={dataLayers.showPricing ? servedPricingRow : null} />
        {dataLayers.observationLabel ? (
          <p className="campaign-strategy-microsite-proposal-note campaign-strategy-microsite-data-layers-observation">
            {describeDataLayersObservation(dataLayers)}
          </p>
        ) : null}
      </section>
    </div>
  );
}
